import React from 'react';
import { DISPOSITION_ORDER, dispositionMeta } from '@/lib/dispositions';
import { TICKETS } from '@/constants/testIds';
import { Inbox, User as UserIcon, CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function TicketsSidebar({
  scope, setScope, counts, currentDisposition, onSelectDisposition, followupToday, onToggleFollowupToday,
}) {
  return (
    <aside
      className="w-[240px] shrink-0 border-r border-stone-200 bg-white flex flex-col overflow-hidden"
      data-testid={TICKETS.sidebar}
    >
      {/* Scope toggle */}
      <div className="p-2 border-b border-stone-200 grid grid-cols-2 gap-1">
        <button
          onClick={() => setScope('assigned')}
          data-testid={TICKETS.sidebarScopeAssigned}
          className={cn(
            "flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors",
            scope === 'assigned' ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
          )}
        >
          <Inbox className="w-3.5 h-3.5" /> Assigned
        </button>
        <button
          onClick={() => setScope('mine')}
          data-testid={TICKETS.sidebarScopeMine}
          className={cn(
            "flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors",
            scope === 'mine' ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
          )}
        >
          <UserIcon className="w-3.5 h-3.5" /> My
        </button>
      </div>

      <div className="overflow-auto hc-scrollbar flex-1">
        <button
          onClick={onToggleFollowupToday}
          data-testid="tickets-followup-today-tab"
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b border-stone-100 transition-colors",
            followupToday ? "bg-amber-50 text-amber-900 border-l-4 border-l-amber-600" : "hover:bg-amber-50/60 text-stone-800"
          )}
        >
          <CalendarCheck className="w-4 h-4 text-amber-600" />
          <span className="flex-1 text-left">Today's Follow-ups</span>
          <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded tabular-nums font-bold" data-testid="tickets-followup-today-count">
            {counts.followup_today ?? 0}
          </span>
        </button>
        <button
          onClick={() => onSelectDisposition('')}
          data-testid={TICKETS.dispositionItem('all')}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b border-stone-100 hover:bg-stone-50",
            !currentDisposition && !followupToday && "bg-stone-100 text-stone-900 border-l-4 border-l-stone-900"
          )}
        >
          <Inbox className="w-4 h-4 text-stone-500" />
          <span className="flex-1 text-left">All Patients</span>
          <span className="text-xs bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded tabular-nums font-bold">
            {counts.total || 0}
          </span>
        </button>

        <ul className="py-1">
          {DISPOSITION_ORDER.map((key) => {
            const m = dispositionMeta(key);
            const Icon = m.icon;
            const count = counts.by_disposition?.[key] ?? 0;
            const active = currentDisposition === key;
            return (
              <li key={key}>
                <button
                  onClick={() => onSelectDisposition(key)}
                  data-testid={TICKETS.dispositionItem(key)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-stone-50 border-l-4 transition-colors",
                    active
                      ? "bg-stone-50 border-l-stone-900 font-semibold text-stone-900"
                      : "border-l-transparent text-stone-700"
                  )}
                  title={m.label}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: m.text }} />
                  <span className="flex-1 text-left truncate">{m.label}</span>
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded tabular-nums font-bold",
                    count > 0 ? "bg-stone-200 text-stone-800" : "bg-transparent text-stone-400"
                  )}>
                    {count}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}
